import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { faBell, faCodeBranch, faEye } from '@fortawesome/pro-solid-svg-icons';
import Keycloak from 'keycloak-js';
import { Subject, combineLatest, map, takeUntil } from 'rxjs';
import { Note, NoteCollection } from 'src/app/rgs/components/objects/objectItem/objectInspection/models/note.model';
import { NoteService } from 'src/app/rgs/components/objects/objectItem/objectInspection/services/note.service';
import { PermissionsService } from 'src/app/shared/services/permissions.service';
import { ArrayUtils } from 'src/app/shared/utils/array.utils';
import { NotesSidebarService } from './services/notes-sidebar.service';
import { Drawer } from 'primeng/drawer';
import { Tag } from 'primeng/tag';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Checkbox } from 'primeng/checkbox';
import { Tooltip } from 'primeng/tooltip';
import { AsyncPipe, TitleCasePipe, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'app-notifications-sidebar',
  templateUrl: './notifications-sidebar.component.html',
  styleUrls: ['./notifications-sidebar.component.scss'],
  animations: [
    trigger('expandCollapseMessage', [
      state(
        'collapsed',
        style({
          height: '1.5em',
        }),
      ),
      state(
        'expanded',
        style({
          height: '*',
        }),
      ),
      transition('collapsed <=> expanded', animate('500ms ease-in-out')),
    ]),
    trigger('expandCollapseFooter', [
      state(
        'collapsed',
        style({
          height: '0',
        }),
      ),
      state(
        'expanded',
        style({
          height: '*',
        }),
      ),
      transition('collapsed <=> expanded', animate('200ms ease-in-out')),
    ]),
  ],
  imports: [Drawer, Tag, FaIconComponent, Checkbox, Tooltip, AsyncPipe, TitleCasePipe, DatePipe, TranslateModule],
})
export class NotificationsSidebarComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly keycloak = inject(Keycloak);
  private readonly permissionsService = inject(PermissionsService);
  private readonly noteService = inject(NoteService);

  private readonly notesSidebarService = inject(NotesSidebarService);

  public readonly faBell = faBell;

  public destroy$ = new Subject<void>();
  public notificationsSidebarVisible = false;
  public notes$ = this.notesSidebarService.items$.pipe(
    map((notes) =>
      notes.filter(
        (note) =>
          !ArrayUtils.ensureArray(note?.gelezen)
            .map((read) => read.gebruiker)
            .includes(this.userName),
      ),
    ),
  );

  public notesAnimationState = new Map<string, boolean>();

  public ngOnInit(): void {
    this.loadData();
    this.handleRemoveOrAddNoteIfRead();
    this.setInitialStateNotesAnimationState();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadData() {
    this.notesSidebarService.findUnreadNotes();
  }

  public setInitialStateNotesAnimationState() {
    this.notes$.pipe(takeUntil(this.destroy$)).subscribe((notes) => {
      notes.forEach((note) => {
        this.notesAnimationState.set(note.id, false);
      });
    });
  }

  public onNoteMouseEnter(note: Note) {
    this.notesAnimationState.set(note.id, true);
  }

  public onNoteMouseLeave(note: Note) {
    this.notesAnimationState.set(note.id, false);
  }

  public handleRemoveOrAddNoteIfRead() {
    combineLatest([this.notesSidebarService.items$, this.noteService.items$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([sidebarNotes, singleChatNotes]) => {
        sidebarNotes.forEach((sidebarNote) => {
          const singleChatNote = singleChatNotes.find((singleChatNote) => singleChatNote.id === sidebarNote.id);

          if (singleChatNote && JSON.stringify(singleChatNote?.gelezen) !== JSON.stringify(sidebarNote?.gelezen)) {
            this.notesSidebarService.updateItemsValues([
              ...sidebarNotes.filter((item) => item.id !== singleChatNote.id),
              singleChatNote,
            ]);
          }
        });
      });
  }

  public showDayDivider(previousNote: Note, actualNote: Note) {
    if (!previousNote) {
      return true;
    }
    if (previousNote?._metadata?.created && actualNote?._metadata?.created) {
      const previousDate = new Date(previousNote?._metadata?.created);
      const actualDate = new Date(actualNote?._metadata?.created);

      if (
        previousDate?.getDay() !== actualDate?.getDay() ||
        previousDate?.getMonth() !== actualDate?.getMonth() ||
        previousDate?.getFullYear() !== actualDate?.getFullYear()
      ) {
        return true;
      }
      return false;
    }
    return true;
  }

  public goTo(note: Note) {
    switch (note.collectie) {
      case NoteCollection.INSPECTION:
        this.router.navigate(
          ['objects', note.rgsObjectId, 'inspections', 'chat', NoteCollection.INSPECTION, note.naam],
          {
            fragment: note.id,
          },
        );
        break;
      case NoteCollection.SCENARIO:
        this.router.navigate(['objects', note.rgsObjectId, 'scenario', 'chat', NoteCollection.SCENARIO, note.naam], {
          fragment: note.id,
        });
        break;
    }
  }

  public getIconByCollection(collection: NoteCollection) {
    switch (collection) {
      case NoteCollection.INSPECTION:
        return faEye;
      case NoteCollection.SCENARIO:
        return faCodeBranch;
    }
  }

  public markAsRead(event: any, note: Note) {
    const readUpdated = [...note.gelezen];
    const index = readUpdated.findIndex((read) => read.gebruiker === this.userName);

    if (index >= 0) {
      if (!event.checked) {
        readUpdated.splice(index, 1);
      }
    } else {
      if (event.checked) {
        readUpdated.push({
          bedrijf: this.license,
          gebruiker: this.userName,
          tijdstip: new Date(),
        });
      }
    }

    let noteUpdated: Note | any = {
      ...note,
      gelezen: readUpdated,
    };
    if (!('_links' in (note as any))) {
      const baseSelfUrl = (note as any)?.links[0]?.href;

      noteUpdated = {
        ...noteUpdated,
        _links: {
          note: {
            href: `${baseSelfUrl}/note/${note.id}`,
          },
          self: {
            href: `${baseSelfUrl}/note/${note.id}`,
          },
        },
      };
    }

    this.notesSidebarService.updateItem(noteUpdated);
  }

  public get license() {
    return this.permissionsService.license as string;
  }

  public get userName() {
    return this.keycloak.idTokenParsed?.['name'];
  }
}
